"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Button,
    Avatar,
    Spinner,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    useDisclosure,
    Chip,
    Input,
    Tabs,
    Tab,
    RadioGroup,
    Radio,
} from "@nextui-org/react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ROUTES } from "@/routes/appRoutes";
import useWindowSize from "@/helpers/hooks/useWindowSize";
import { useAuth } from "@/providers/authProvider";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";

// Icons
import BottomArrow from "@/helpers/svg/bottomArrow.svg";
import ProfileIcon from "@/helpers/svg/profileIcon.svg";
import SwitchProfileIcon from "@/helpers/svg/switch-profile.svg";
import SettingsIcon from "@/helpers/svg/settingsIcons.svg";
import FavouritesIcon from "@/helpers/svg/favouritesIcon.svg";
import HistoryIcon from "@/helpers/svg/historyIcon.svg";
import ProfileChips from "@/helpers/svg/profileChips.svg";
import Logout from "@/helpers/svg/logoutIcon.svg";
import BuySubscriptionIcon from "@/helpers/svg/buySubscription.svg";
import QrScanIcon from "@/helpers/svg/qrScanIcon.svg";
import TvLoginModal from "@/components/common/TvLoginModal";

import "./UserDropdown.css";
import { switchProfile } from "@/services/authService";
import { addProfile } from "@/services/userService";
import { enqueueSnackbar } from "notistack";
import { getErrorMessage } from "@/lib/errorHandler";
import { trackGtagEvent } from "@/services/gtagAnalyticsService";
import { getProfileImageUrl } from "@/helpers/api.util";

const MAX_PROFILES = 3;

// ============================================================================
// Shared dropdown content (used in both desktop popover and mobile bottom sheet)
// ============================================================================

function DropdownContent({ onClose, onLogout, onTvLoginOpen }) {
    const router = useRouter();
    const {
        login: authLogin,
        user,
        activeProfile,
        setActiveProfile,
        refreshUserProfile,
    } = useAuth();

    const { canPlayLoading: subLoading, daysRemaining: remainingSubs } = useSubscriptionStatus();

    const [profilesOpen, setProfilesOpen] = useState(false);
    const [switchLoading, setSwitchLoading] = useState(null);
    const [addProfileOpen, setAddProfileOpen] = useState(false);
    const [newProfileName, setNewProfileName] = useState("");
    const [newProfileAgeRange, setNewProfileAgeRange] = useState("120");
    const [newProfileImageFile, setNewProfileImageFile] = useState(null);
    const [newProfileImagePreview, setNewProfileImagePreview] = useState(null);
    const [addProfileLoading, setAddProfileLoading] = useState(false);
    const fileInputRef = useRef(null);

    const profiles = user?.profiles || [];
    // Active profile: from auth context, or fall back to first profile
    const activeProfileId = activeProfile?._id || profiles?.[0]?._id;

    const resetAddProfileForm = useCallback(() => {
        setNewProfileName("");
        setNewProfileAgeRange("120");
        setNewProfileImageFile(null);
        setNewProfileImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    }, []);

    const handleAddProfileOpen = () => {
        if (profiles.length >= MAX_PROFILES) {
            enqueueSnackbar("حداکثر تعداد پروفایل‌ها ساخته شده است", { variant: "warning" });
            return;
        }
        setAddProfileOpen(true);
    };

    const handleAddProfileClose = useCallback(() => {
        if (addProfileLoading) return;
        setAddProfileOpen(false);
        resetAddProfileForm();
    }, [addProfileLoading, resetAddProfileForm]);

    const handleImageSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setNewProfileImageFile(file);
        const reader = new FileReader();
        reader.onloadend = () => setNewProfileImagePreview(reader.result);
        reader.readAsDataURL(file);
    };

    const clearImage = (e) => {
        e.stopPropagation();
        setNewProfileImageFile(null);
        setNewProfileImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleAddProfileSubmit = async () => {
        const fullName = newProfileName.trim();
        if (!fullName) {
            enqueueSnackbar("نام پروفایل را وارد کنید", { variant: "warning" });
            return;
        }

        setAddProfileLoading(true);
        try {
            await addProfile({
                fullName,
                ageRange: Number(newProfileAgeRange),
                image: newProfileImageFile || undefined,
            });
            trackGtagEvent("profile_created", {
                source: "user_dropdown",
                age_range: Number(newProfileAgeRange),
                has_image: Boolean(newProfileImageFile),
            });
            await refreshUserProfile?.();
            enqueueSnackbar("پروفایل جدید ساخته شد", { variant: "success" });
            setProfilesOpen(true);
            setAddProfileOpen(false);
            resetAddProfileForm();
        } catch (err) {
            enqueueSnackbar(
                getErrorMessage(err) || "خطا در ساخت پروفایل",
                { variant: "error" }
            );
        } finally {
            setAddProfileLoading(false);
        }
    };

    const handleSwitchProfile = async (profileId) => {
        if (profileId === activeProfileId || switchLoading) return;
        setSwitchLoading(profileId);
        try {
            const result = await switchProfile(profileId);
            const targetProfile = profiles.find((p) => p._id === profileId);
            await authLogin(
                result.accessToken,
                result.refreshToken,
                targetProfile || null
            );
            if (targetProfile) setActiveProfile(targetProfile);
            trackGtagEvent("profile_selected", {
                source: "user_dropdown",
                profile_id: profileId,
            });
            enqueueSnackbar("پروفایل تغییر کرد", { variant: "success" });
            onClose();
            router.refresh();
        } catch (err) {
            enqueueSnackbar(
                getErrorMessage(err) || "خطا در تغییر پروفایل",
                { variant: "error" }
            );
        } finally {
            setSwitchLoading(null);
        }
    };

    // Navigation helpers
    const navigate = useCallback(
        (path) => {
            onClose();
            router.push(path);
        },
        [onClose, router]
    );

    return (
        <>
            {/* ---- Header: subscription ---- */}
            <div className="ud-header flex flex-col items-center">
                {subLoading ? (
                    <Spinner color="danger" size="sm" />
                ) : (
                    <>
                        <div className="mb-2">
                            {remainingSubs ? (
                                <Chip color="success" size="sm" variant="flat">اشتراک دارید</Chip>
                            ) : (
                                <Chip color="danger" size="sm" variant="flat">بدون اشتراک</Chip>
                            )}
                        </div>

                        {remainingSubs ? (
                            <div className="ud-sub-days mb-3">{remainingSubs} روز باقی‌مانده</div>
                        ) : null}

                        <Link
                            href={ROUTES.subscription}
                            onClick={onClose}
                            className="ud-buy-sub-btn !mb-0"
                        >
                            {remainingSubs ? "تمدید اشتراک" : "خرید اشتراک"}
                            <BuySubscriptionIcon />
                        </Link>
                    </>
                )}
            </div>

            {/* ---- Menu items ---- */}
            <div className="ud-menu">
                {/* ---- Profile switcher ---- */}
                <>
                    <button
                        className="ud-menu-item"
                        onClick={() => setProfilesOpen((v) => !v)}
                    >
                        <span className="ud-menu-icon">
                            <SwitchProfileIcon />
                        </span>
                        <span className="ud-menu-label">تغییر پروفایل</span>
                        <span className={`ud-menu-arrow${profilesOpen ? " ud-rotated" : ""}`}>
                            <BottomArrow />
                        </span>
                    </button>

                    <div className={`ud-profiles-section${profilesOpen ? " ud-profiles-open" : ""}`}>
                        <div className="ud-profiles-list">
                            {profiles.map((profile, index) => {
                                const isActive = profile._id === activeProfileId;
                                const isLoading = switchLoading === profile._id;
                                return (
                                    <button
                                        key={profile._id}
                                        className={`ud-profile-item${isActive ? " ud-profile-active" : ""}`}
                                        onClick={() => handleSwitchProfile(profile._id)}
                                        disabled={isActive || isLoading}
                                    >
                                        <Avatar
                                            size="sm"
                                            name={profile.fullName?.charAt(0)}
                                            src={getProfileImageUrl(profile.profileImage) || undefined}
                                            classNames={{
                                                base: `bg-gradient-to-br ${index === 0
                                                    ? "from-[#850000] to-[#c0392b]"
                                                    : index === 1
                                                        ? "from-[#1a237e] to-[#3f51b5]"
                                                        : "from-[#006064] to-[#00acc1]"
                                                    } text-white`,
                                            }}
                                        />
                                        <span className="ud-profile-name">{profile.fullName}</span>
                                        {isActive && (
                                            <span className="ud-profile-badge">فعال</span>
                                        )}
                                        {isLoading && (
                                            <Spinner size="sm" color="danger" className="mr-auto" />
                                        )}
                                    </button>
                                );
                            })}

                            <button
                                className="ud-profile-item ud-profile-add"
                                onClick={handleAddProfileOpen}
                                disabled={profiles.length >= MAX_PROFILES}
                            >
                                <span className="ud-profile-add-icon">+</span>
                                <span className="ud-profile-name">عضو جدید</span>
                                {profiles.length >= MAX_PROFILES && (
                                    <span className="ud-profile-badge">حداکثر</span>
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="ud-divider" />
                </>

                {/* Account */}
                <button
                    className="ud-menu-item"
                    onClick={() => navigate(ROUTES.profile.account)}
                >
                    <span className="ud-menu-icon">
                        <ProfileIcon />
                    </span>
                    <span className="ud-menu-label">حساب کاربری</span>
                    <span className="ud-menu-icon">
                        <SettingsIcon />
                    </span>
                </button>

                <div className="ud-divider" />

                {/* Favourites */}
                <button
                    className="ud-menu-item"
                    onClick={() => navigate(ROUTES.profile.favourites)}
                >
                    <span className="ud-menu-icon">
                        <FavouritesIcon />
                    </span>
                    <span className="ud-menu-label">نشان شده</span>
                </button>

                {/* Watch history */}
                <button
                    className="ud-menu-item"
                    onClick={() => navigate(ROUTES.profile.history)}
                >
                    <span className="ud-menu-icon">
                        <HistoryIcon />
                    </span>
                    <span className="ud-menu-label">تاریخچه تماشا</span>
                </button>

                {/* Zheton */}
                <button
                    className="ud-menu-item"
                    onClick={() => navigate(ROUTES.zheton)}
                >
                    <span className="ud-menu-icon">
                        <ProfileChips />
                    </span>
                    <span className="ud-menu-label">ژتون</span>
                </button>

                <div className="ud-divider" />

                {/* ---- QR / TV Login ---- */}
                <button
                    className="ud-menu-item"
                    onClick={() => {
                        onClose();
                        setTimeout(() => onTvLoginOpen(), 150);
                    }}
                >
                    <span className="ud-menu-icon">
                        <QrScanIcon size={18} color="#BFBFBF" />
                    </span>
                    <span className="ud-menu-label">ورود با کد تلویزیون</span>
                </button>

                <div className="ud-divider" />

                {/* Logout */}
                <button
                    className="ud-menu-item ud-logout"
                    onClick={onLogout}
                >
                    <span className="ud-menu-icon">
                        <Logout />
                    </span>
                    <span className="ud-menu-label">خروج</span>
                </button>
            </div>

            <Modal
                isOpen={addProfileOpen}
                onClose={handleAddProfileClose}
                placement="center"
                backdrop="blur"
                classNames={{
                    base: "ud-add-profile-modal",
                    header: "ud-add-profile-header",
                    body: "ud-add-profile-body",
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        <span className="ud-add-profile-title">افزودن عضو جدید</span>
                        <span className="ud-add-profile-subtitle">اطلاعات عضو جدید را وارد کنید</span>
                    </ModalHeader>
                    <ModalBody>
                        <div className="ud-add-profile-photo">
                            <label className="ud-add-profile-label">تصویر پروفایل (اختیاری)</label>
                            <div
                                className="ud-add-profile-avatar"
                                style={{
                                    background: newProfileImagePreview
                                        ? `url(${newProfileImagePreview}) center/cover no-repeat`
                                        : "rgba(255, 255, 255, 0.06)",
                                    border: newProfileImagePreview
                                        ? "2px solid rgba(133, 0, 0, 0.5)"
                                        : "2px dashed rgba(255, 255, 255, 0.15)",
                                }}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {!newProfileImagePreview && (
                                    <span className="ud-add-profile-camera">+</span>
                                )}
                                {newProfileImagePreview && (
                                    <button
                                        type="button"
                                        onClick={clearImage}
                                        className="ud-add-profile-clear"
                                        aria-label="حذف تصویر"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                className="hidden"
                                onChange={handleImageSelect}
                            />
                        </div>

                        <div>
                            <label className="ud-add-profile-label">نام کاربر</label>
                            <Input
                                placeholder="نام"
                                size="lg"
                                value={newProfileName}
                                onChange={(e) => setNewProfileName(e.target.value)}
                                classNames={{
                                    inputWrapper: "ud-add-profile-input-wrapper",
                                    input: "ud-add-profile-input",
                                }}
                            />
                        </div>

                        <div>
                            <label className="ud-add-profile-label">رده سنی</label>
                            <Tabs
                                aria-label="Age options"
                                selectedKey={newProfileAgeRange === "120" ? "adult" : "parental"}
                                onSelectionChange={(key) => {
                                    if (key === "adult") setNewProfileAgeRange("120");
                                    if (key === "parental" && newProfileAgeRange === "120") {
                                        setNewProfileAgeRange("7");
                                    }
                                }}
                                color="primary"
                                variant="bordered"
                                fullWidth
                                classNames={{
                                    tabList: "ud-add-profile-tab-list",
                                    cursor: "hidden",
                                    tab: "ud-add-profile-tab",
                                    tabContent: "ud-add-profile-tab-content",
                                    panel: "w-full pt-4",
                                }}
                            >
                                <Tab
                                    key="adult"
                                    title={<span className="ud-add-profile-tab-title">بزرگسال</span>}
                                />
                                <Tab
                                    key="parental"
                                    title={<span className="ud-add-profile-tab-title">با نظارت والدین</span>}
                                >
                                    <RadioGroup
                                        orientation="horizontal"
                                        value={newProfileAgeRange}
                                        onValueChange={setNewProfileAgeRange}
                                        classNames={{
                                            wrapper: "ud-add-profile-radio-group",
                                        }}
                                    >
                                        <Radio classNames={{ label: "ud-add-profile-radio-label" }} value="7">
                                            تا ۷ سال
                                        </Radio>
                                        <Radio classNames={{ label: "ud-add-profile-radio-label" }} value="13">
                                            تا ۱۳ سال
                                        </Radio>
                                        <Radio classNames={{ label: "ud-add-profile-radio-label" }} value="18">
                                            تا ۱۸ سال
                                        </Radio>
                                    </RadioGroup>
                                </Tab>
                            </Tabs>
                        </div>

                        <Button
                            size="lg"
                            fullWidth
                            color="primary"
                            isLoading={addProfileLoading}
                            className="ud-add-profile-submit"
                            onClick={handleAddProfileSubmit}
                        >
                            ایجاد پروفایل
                        </Button>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
}

// ============================================================================
// ============================================================================
// Desktop dropdown (no Popover — avoids focus management scroll issues)
// ============================================================================

function DesktopDropdown({ isOpen, setIsOpen, handleClose, handleLogoutRequest, onTvLoginOpen }) {
    const containerRef = useRef(null);

    // Click-outside handler
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (e) => {
            if (e.target.closest?.(".ud-add-profile-modal")) return;
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };

        // Delay adding listener so the opening click doesn't immediately close it
        const id = requestAnimationFrame(() => {
            document.addEventListener("mousedown", handleClickOutside);
        });

        return () => {
            cancelAnimationFrame(id);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, setIsOpen]);

    return (
        <div ref={containerRef} className="relative z-50">
            <Button
                color="default"
                variant="light"
                disableRipple
                className="ud-user-trigger !px-0 lg:px-4 min-w-0 gap-0 !bg-transparent"
                onPress={() => setIsOpen((v) => !v)}
            >
                <Avatar
                    classNames={{ base: "bg-transparent text-primary-white" }}
                />
                <BottomArrow />
            </Button>

            {/* Dropdown panel */}
            <div
                style={{
                    position: "absolute",
                    top: "calc(100% + 10px)",
                    left: "50%",
                    transform: "translateX(-50%)",
                    opacity: isOpen ? 1 : 0,
                    pointerEvents: isOpen ? "auto" : "none",
                    transition: "opacity 0.2s ease, transform 0.2s ease",
                    zIndex: 9999,
                }}
            >
                {isOpen && (
                    <div className="user-dropdown">
                        <DropdownContent onClose={handleClose} onLogout={handleLogoutRequest} onTvLoginOpen={onTvLoginOpen} />
                    </div>
                )}
            </div>
        </div>
    );
}

// ============================================================================
// Main export
// ============================================================================

export default function UserDropdown() {
    const device = useWindowSize();
    const router = useRouter();
    const { logout: authLogout } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [logoutLoading, setLogoutLoading] = useState(false);

    const {
        isOpen: logoutModalOpen,
        onOpen: onLogoutOpen,
        onClose: onLogoutClose,
    } = useDisclosure();

    const {
        isOpen: tvLoginModalOpen,
        onOpen: onTvLoginOpen,
        onClose: onTvLoginClose,
    } = useDisclosure();

    const isMobile = device === "SM" || device === "MD";

    const handleClose = useCallback(() => setIsOpen(false), []);

    // Close the dropdown/popover first, then open the logout modal
    const handleLogoutRequest = useCallback(() => {
        setIsOpen(false);
        // Small delay to let the dropdown/popover close before opening the modal
        setTimeout(() => {
            onLogoutOpen();
        }, 150);
    }, [onLogoutOpen]);

    // Logout
    const handleLogout = async () => {
        try {
            setLogoutLoading(true);
            await authLogout();
            onLogoutClose();
            const path = window.location.pathname;
            if (
                path.includes("/profile") ||
                path.includes("/zheton") ||
                path.includes("/player")
            ) {
                router.push("/");
            }
        } catch (error) {
            console.error("Logout error:", error);
        } finally {
            setLogoutLoading(false);
        }
    };

    // ---------- Logout confirmation modal (rendered at top level) ----------
    const logoutModal = (
        <Modal
            isOpen={logoutModalOpen}
            size="lg"
            onClose={onLogoutClose}
            backdrop="blur"
            className="bg-[#262626] blur-none"
        >
            <ModalContent>
                <ModalBody className="p-12 min-h-[40vh] md:min-h-fit ud-logout-modal">
                    <h2>آیا میخواهید از حساب کاربری خود خارج شوید؟</h2>
                    <div className="w-full flex flex-col md:flex-row flex-wrap gap-3 md:flex-nowrap justify-center items-center lg:mt-5">
                        <Button
                            color="primary"
                            radius="full"
                            fullWidth
                            size="lg"
                            className="text-primary-white flex-auto w-full text-lg"
                            onClick={handleLogout}
                            isLoading={logoutLoading}
                        >
                            بلی
                        </Button>
                        <Button
                            fullWidth
                            size="lg"
                            variant="light"
                            radius="full"
                            color="#BFBFBF"
                            className="text-primary-white flex-auto w-full text-lg"
                            onClick={onLogoutClose}
                            disabled={logoutLoading}
                        >
                            خیر
                        </Button>
                    </div>
                </ModalBody>
            </ModalContent>
        </Modal>
    );

    const tvLoginModalEl = (
        <TvLoginModal isOpen={tvLoginModalOpen} onClose={onTvLoginClose} />
    );

    // ---------- Desktop: Simple Dropdown ----------
    if (!isMobile) {
        return (
            <>
                <DesktopDropdown
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    handleClose={handleClose}
                    handleLogoutRequest={handleLogoutRequest}
                    onTvLoginOpen={onTvLoginOpen}
                />
                {logoutModal}
                {tvLoginModalEl}
            </>
        );
    }

    // ---------- Mobile: Bottom Sheet ----------
    return (
        <>
            <Button
                color="default"
                variant="light"
                disableRipple
                className="ud-user-trigger !px-0 min-w-0 gap-0 !bg-transparent z-50 relative"
                onClick={() => setIsOpen(true)}
            >
                <Avatar classNames={{ base: "bg-transparent text-primary-white" }} />
            </Button>

            <Modal
                isOpen={isOpen}
                onClose={handleClose}
                placement="bottom"
                backdrop="blur"
                size="full"
                className="ud-bottom-sheet"
                classNames={{
                    base: "!my-0 !mx-0 !rounded-b-none !rounded-t-2xl !max-h-[85vh] !bg-[#161616]",
                    wrapper: "items-end",
                    closeButton: "!top-3 !left-3 !right-auto !z-20 !bg-[#343434] !text-white !opacity-100 border border-white/15 shadow-lg hover:!bg-[#454545] focus:!bg-[#454545]",
                }}
                motionProps={{
                    variants: {
                        enter: {
                            y: 0,
                            opacity: 1,
                            transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                        },
                        exit: {
                            y: "100%",
                            opacity: 0,
                            transition: { duration: 0.25, ease: [0.4, 0, 0.2, 1] },
                        },
                    },
                }}
            >
                <ModalContent>
                    <ModalBody className="!px-0 !pt-0 !pb-4 overflow-y-auto">
                        <div className="ud-sheet-inner">
                            <div className="ud-drag-handle">
                                <span />
                            </div>
                            <DropdownContent onClose={handleClose} onLogout={handleLogoutRequest} onTvLoginOpen={onTvLoginOpen} />
                        </div>
                    </ModalBody>
                </ModalContent>
            </Modal>

            {logoutModal}
            {tvLoginModalEl}
        </>
    );
}
